<script lang="ts">
	import { page } from '$app/stores';
	import { t, type Language } from '$lib/i18n';
	import rygene from '$lib/assets/clients/rygene.png';

	let lang = $derived($page.params.lang as Language);
	let tr = $derived(t(lang));
</script>

<svelte:head>
	<title>{tr('case_rygene_title')} | Ameta</title>
	<meta name="description" content={tr('case_rygene_meta_desc')} />
</svelte:head>

<article class="py-20 md:py-32">
	<div class="max-w-4xl mx-auto px-6">
		<!-- Back link -->
		<a
			href="/{lang}"
			class="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-12"
		>
			<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
			</svg>
			{tr('case_back_home')}
		</a>

		<!-- Header -->
		<header class="mb-16">
			<p class="text-ameta-green text-sm font-medium uppercase tracking-wider mb-4">
				{tr('case_label')}
			</p>
			<h1 class="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
				{tr('case_rygene_headline')}
			</h1>
			<div class="flex items-center gap-4">
				<img
					src={rygene}
					alt="Rygene-Smith & Thommesen"
					class="h-10 w-auto brightness-0 invert opacity-70 client-logo"
				/>
				<span class="text-slate-400">|</span>
				<span class="text-slate-400">{tr('case_rygene_industry')}</span>
			</div>
		</header>

		<!-- Challenge -->
		<section class="mb-16">
			<h2 class="text-2xl font-semibold text-white mb-4">{tr('case_challenge_title')}</h2>
			<p class="text-slate-300 text-lg leading-relaxed mb-6">
				{tr('case_rygene_challenge_1')}
			</p>
			<blockquote class="border-l-2 border-ameta-green pl-6 my-8">
				<p class="text-xl text-slate-200 italic mb-4">
					"{tr('case_rygene_quote_1')}"
				</p>
				<cite class="text-slate-400 not-italic">— Elisabeth Berli, {tr('case_rygene_role')}</cite>
			</blockquote>
		</section>

		<!-- Solution -->
		<section class="mb-16">
			<h2 class="text-2xl font-semibold text-white mb-4">{tr('case_solution_title')}</h2>
			<p class="text-slate-300 text-lg leading-relaxed mb-6">
				{tr('case_rygene_solution_1')}
			</p>
			<ul class="space-y-3 text-slate-300">
				<li class="flex items-start gap-3">
					<svg
						class="w-5 h-5 text-ameta-green mt-1 shrink-0"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M5 13l4 4L19 7"
						/>
					</svg>
					{tr('case_rygene_result_1')}
				</li>
				<li class="flex items-start gap-3">
					<svg
						class="w-5 h-5 text-ameta-green mt-1 shrink-0"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M5 13l4 4L19 7"
						/>
					</svg>
					{tr('case_rygene_result_2')}
				</li>
				<li class="flex items-start gap-3">
					<svg
						class="w-5 h-5 text-ameta-green mt-1 shrink-0"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M5 13l4 4L19 7"
						/>
					</svg>
					{tr('case_rygene_result_3')}
				</li>
				<li class="flex items-start gap-3">
					<svg
						class="w-5 h-5 text-ameta-green mt-1 shrink-0"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M5 13l4 4L19 7"
						/>
					</svg>
					{tr('case_rygene_result_4')}
				</li>
			</ul>
		</section>

		<!-- Result quote -->
		<section class="mb-16">
			<h2 class="text-2xl font-semibold text-white mb-4">{tr('case_result_title')}</h2>
			<blockquote class="border-l-2 border-ameta-green pl-6 my-8">
				<p class="text-xl text-slate-200 italic mb-4">
					"{tr('case_rygene_quote_2')}"
				</p>
				<cite class="text-slate-400 not-italic">— Elisabeth Berli, {tr('case_rygene_role')}</cite>
			</blockquote>
			<blockquote class="border-l-2 border-ameta-green pl-6 my-8">
				<p class="text-xl text-slate-200 italic mb-4">
					"{tr('case_rygene_quote_3')}"
				</p>
				<cite class="text-slate-400 not-italic">— Elisabeth Berli, {tr('case_rygene_role')}</cite>
			</blockquote>
		</section>

		<!-- CTA -->
		<section
			class="mt-20 p-8 md:p-12 rounded-2xl bg-slate-800/50 border border-slate-700/50 text-center"
		>
			<h2 class="text-2xl font-semibold text-white mb-4">{tr('case_cta_title')}</h2>
			<p class="text-slate-400 mb-8 max-w-lg mx-auto">{tr('case_cta_desc')}</p>
			<a href="/{lang}/#contact" class="btn-primary">
				{tr('case_cta_button')}
			</a>
		</section>
	</div>
</article>
